import React, { Component } from 'react';
import { hot } from 'react-hot-loader';
import { addEvent, removeEvent, addClass, removeClass } from '../../utils/dom';
import s from './Slider.scss';

class Slider extends Component {
	autoplayPointer = null;

	static getDragX(e) {
		return e?.touches?.[0]?.pageX;
	}

	static getIndex(infinite, childrenLength, index, nextIndex = false) {
		if (nextIndex === false) index += 1;
		else index = nextIndex;

		if (index >= childrenLength + infinite) {
			return 0 + infinite;
		}
		if (index < infinite) {
			return childrenLength + infinite - 1;
		}
		return index;
	}

	constructor(props) {
		super(props);

		this.state = {
			dragStart: 0,
			partialIndex: 0,
			slideWidth: 100,
			index: (props.startIndex + props.infinite) || (0 + props.infinite),
			lastIndex: (props.startIndex + props.infinite) || (0 + props.infinite),
			transition: false,
			disabled: props.children.length === 1,
		};

		this.slider = React.createRef();
		this.sliderInner = React.createRef();
	}

	componentDidMount() {
		const { screenMaxWidthDisable, screenMinWidthDisable, children, autoplay, transitionType, autoplayFilm } = this.props;
		const { index } = this.state;

		const autoplayFunction = () => {
			// Use this.state.index to get the latest updated index
			const nextIndex = Slider.getIndex(this.props.infinite, this.props.children.length, this.state.index);
			this.goToSlide(nextIndex);
		};

		if (autoplay && children.length > 1) {
			this.autoplayPointer = window.setInterval(autoplayFunction, autoplay);

			addEvent(this.sliderInner.current, 'click.slider', () => {
				window.clearInterval(this.autoplayPointer);
			}, { passive: false });
		}

		const elementsInitiated = () => {
			if (!this.sliderInner.current) {
				setTimeout(() => {
					elementsInitiated();
				}, 50);
			}

			const currentElement = this.sliderInner.current?.children[0]?.children?.[index];
			if (currentElement) {
				if (transitionType === 'fade') addClass(this.sliderInner.current.children[0].children[index], 'u_selected');

				const videoElements = currentElement.querySelectorAll('video');
				if (videoElements.length > 0 && autoplayFilm) {
					[...videoElements].forEach((videoElement) => {
						videoElement.play();
					});
				}
			}
		};

		elementsInitiated();

		if ((screenMaxWidthDisable !== false || screenMinWidthDisable !== false) && children.length > 1) {
			let isDisabled = true;
			let firstRun = true;

			const resizeEvent = () => {
				const width = window.innerWidth;

				// Only one child or less || Screen is over max width || Screen is under min width -> disable
				const disable = children.length <= 1
					|| (screenMaxWidthDisable !== false && width > screenMaxWidthDisable)
					|| (screenMinWidthDisable !== false && width < screenMinWidthDisable);

				// More than one child && Screen is over max width if set || Screen is under min width if set -> enable
				const enable = children.length > 1
					&& ((screenMaxWidthDisable !== false && width <= screenMaxWidthDisable) || screenMaxWidthDisable === false)
					&& ((screenMinWidthDisable !== false && width >= screenMinWidthDisable) || screenMinWidthDisable === false);

				if (disable && (!isDisabled || firstRun)) {
					isDisabled = true;

					if (autoplay) window.clearInterval(this.autoplayPointer);
					this.setState({
						disabled: true,
					});

					removeEvent(document.body, 'touchmove.slider', { passive: false });
				} else if (enable && (isDisabled || firstRun)) {
					isDisabled = false;

					if (autoplay) this.autoplayPointer = window.setInterval(autoplayFunction, autoplay);
					this.setState({
						disabled: false,
					});

					removeEvent(document.body, 'touchmove.slider', { passive: false });
					addEvent(document.body, 'touchmove.slider', (e) => {
						if (this.sliderInner.current && this.sliderInner.current.contains(e.target)) {
							if (Math.abs(e.touches[0].pageX - this.state.dragStart) > 20) {
								e.preventDefault();
							}
						}
					}, { passive: false });
				}
				firstRun = false;
			};

			addEvent(window, 'resize.slider', () => {
				window.requestAnimationFrame(resizeEvent);
			}, { passive: false });

			resizeEvent();
		} else {
			// Never disabled prevent scrolling always
			removeEvent(document.body, 'touchmove.slider', { passive: false });
			addEvent(document.body, 'touchmove.slider', (e) => {
				if (this.sliderInner.current && this.sliderInner.current.contains(e.target)) {
					if (Math.abs(e.touches[0].pageX - this.state.dragStart) > 20) {
						e.preventDefault();
					}
				}
			}, { passive: false });
		}
	}

	componentDidUpdate(prevProps) {
		// Restart location
		if (prevProps.location && prevProps.location !== this.props.location) this.goToSlide((this.props.infinite + 0), false, false);
	}

	componentWillUnmount() {
		window.clearInterval(this.autoplayPointer);
		removeEvent(this.sliderInner.current, 'click.slider', { passive: false });
		removeEvent(document.body, 'touchmove.slider', { passive: false });
		removeEvent(window, 'resize.slider', { passive: false });
	}

	handleDragStart = (e) => {
		const x = Slider.getDragX(e);

		if (this.props.autoplay) window.clearInterval(this.autoplayPointer);

		this.setState({
			dragStart: x,
			transition: false,
			partialIndex: 0,
			slideWidth: this.slider.current.offsetWidth,
		});
	}

	handleDragMove = (e) => {
		const { dragStart, slideWidth } = this.state;

		const x = Slider.getDragX(e);
		const offset = dragStart - x;
		const partialIndex = offset / slideWidth;

		this.setState({
			partialIndex,
		});
	}

	handleDragEnd = () => {
		const { autoplayFilm, transitionType } = this.props;
		const { partialIndex, index } = this.state;

		// If over 10% then apply transition.
		if (Math.abs(partialIndex * 100) > 20) {
			const nextIndex = partialIndex > 0 ? index + 1 : index - 1;

			// Pause film if previous child had video element.
			const $childCurrentElement = this.sliderInner.current?.children?.[0]?.children?.[index];
			const pauseVideoElements = $childCurrentElement ? $childCurrentElement.querySelectorAll('video') : false;
			if (pauseVideoElements && pauseVideoElements.length > 0) {
				[...pauseVideoElements].forEach((videoElement) => {
					videoElement.pause();
				});
			}

			// Start film if child has video element.
			const $childNextElement = this.sliderInner.current?.children?.[0]?.children?.[nextIndex];
			const playVideoElements = $childNextElement ? $childNextElement.querySelectorAll('video') : false;
			if (playVideoElements && playVideoElements.length > 0 && autoplayFilm) {
				[...playVideoElements].forEach((videoElement) => {
					videoElement.play();
				});
			}

			if (transitionType === 'fade') {
				if (this.sliderInner.current?.children?.[0]?.children?.[index]) removeClass(this.sliderInner.current.children[0].children[index], 'u_selected');
				if (this.sliderInner.current?.children?.[0]?.children?.[nextIndex]) addClass(this.sliderInner.current.children[0].children[nextIndex], 'u_selected');
			}

			this.setState({
				dragStart: 0,
				partialIndex: 0,
				index: nextIndex,
				lastIndex: index,
				transition: true,
			});
		} else {
			this.setState({
				dragStart: 0,
				partialIndex: 0,
				transition: true,
			});
		}
	}

	handleTransitionEnd = () => {
		const { infinite, children } = this.props;
		const { index } = this.state;
		const newIndex = Slider.getIndex(infinite, children.length, false, index);

		if (newIndex !== index) {
			this.setState({
				index: newIndex,
				transition: false,
			});
		}
	}

	handleClick = (e, dir) => {
		e.preventDefault();
		const { lastIndex } = this.state;

		this.goToSlide(lastIndex + dir, e);
	}

	handleArrowClick = (e) => {
		e.preventDefault();
		const { index } = this.state;
		const direction = e.currentTarget.value;
		this.goToSlide(index + parseInt(direction, 10), e);
	}

	handleNavClick = (e) => {
		e.preventDefault();
		const { infinite } = this.props;
		const toIndex = e.currentTarget.value;
		this.goToSlide((parseInt(toIndex, 10) + infinite), e);
	}

	handleThumbClick = (e) => {
		e.preventDefault();
		const { infinite } = this.props;
		const toIndex = e.currentTarget.value;
		this.goToSlide((parseInt(toIndex, 10) + infinite), e);
	}

	goToSlide = (nextIndex, e, transition = true) => {
		const { infinite, children, transitionType, autoplayFilm, autoplay } = this.props;
		const { index } = this.state;

		if (e) {
			e.preventDefault();
			e.stopPropagation();

			if (autoplay) {
				window.clearInterval(this.autoplayPointer);
			}
		}

		if (transitionType === 'fade') {
			nextIndex = Slider.getIndex(infinite, children.length, false, nextIndex);
			if (this.sliderInner.current?.children?.[0]?.children?.[index]) removeClass(this.sliderInner.current.children[0].children[index], 'u_selected');
			if (this.sliderInner.current?.children?.[0]?.children?.[nextIndex]) addClass(this.sliderInner.current.children[0].children[nextIndex], 'u_selected');
		}

		// Pause film if previous child had video element.
		const $childCurrentElement = this.sliderInner.current?.children?.[0]?.children?.[index];
		const pauseVideoElements = $childCurrentElement ? $childCurrentElement.querySelectorAll('video') : false;
		if (pauseVideoElements && pauseVideoElements.length > 0) {
			[...pauseVideoElements].forEach((videoElement) => {
				videoElement.pause();
			});
		}

		// Start film if child has video element.
		const $childNextElement = this.sliderInner.current?.children?.[0]?.children?.[nextIndex];
		const playVideoElements = $childNextElement ? $childNextElement.querySelectorAll('video') : false;
		if (playVideoElements && playVideoElements.length > 0 && autoplayFilm) {
			[...playVideoElements].forEach((videoElement) => {
				videoElement.play();
			});
		}

		this.setState({
			index: nextIndex,
			lastIndex: index,
			transition,
		});
	}

	createThumbs(children) {
		return React.Children.map(children, (child) => {
			if (!React.isValidElement(child)) return child;
			if ((child.type === 'a' || child.type === 'div') && child.props.children) return this.createThumbs(child.props.children);
			const childProps = {};
			if (child.type === 'img') childProps.src = child.props.src.replace('-zoom', '-thumb');
			childProps.children = this.createThumbs(child.props.children);
			return React.cloneElement(child, childProps);
		});
	}

	renderCounter() {
		const { infinite, children } = this.props;
		const { index } = this.state;
		const navIndex = Slider.getIndex(infinite, children.length, false, index);

		return (
			<div className={s.slider_counter}>{ `${navIndex - infinite + 1}/${children.length}` }</div>
		);
	}

	renderNav() {
		const { infinite, children } = this.props;
		const { index } = this.state;
		const navIndex = Slider.getIndex(infinite, children.length, false, index);

		return (
			<div className={s.slider_nav}>
				<div className={s.slider_nav_buttons}>
					{
						children.map((slide, i) => (
							<button type="button" className={[s.slider_nav_button, (i === (navIndex - infinite) ? s.slider_nav_button__active : null)].join(' ')} key={`pagination-${i}`} value={i} onClick={this.handleNavClick} aria-labelledby={`Slider pagination ${i}`} />
						))
					}
				</div>
			</div>
		);
	}

	renderArrows() {
		const { children, infinite } = this.props;
		const { index } = this.state;

		return (
			<div className={s.slider_arrows}>
				<button type="button" className={[s.slider_arrow, s.slider_arrow__left, (index === 0 + infinite ? s.slider_arrow__first : null)].join(' ')} value="-1" onClick={this.handleArrowClick} aria-labelledby="Slider arrow left">
					<svg width="13" height="12" viewBox="0 0 13 12" fill="none" xmlns="http://www.w3.org/2000/svg">
						<path d="M7.87525 0.60791H6.16325L8.14725 3.24791L9.95525 5.56791H0.53125V7.03991H9.95525L8.14725 9.35991L6.16325 11.9999H7.87525L12.1793 6.30391L7.87525 0.60791Z" />
					</svg>
				</button>
				<button type="button" className={[s.slider_arrow, s.slider_arrow__right, (children.length === index - infinite ? s.slider_arrow__last : null)].join(' ')} value="1" onClick={this.handleArrowClick} aria-labelledby="Slider arrow right">
					<svg width="13" height="12" viewBox="0 0 13 12" fill="none" xmlns="http://www.w3.org/2000/svg">
						<path d="M7.87525 0.60791H6.16325L8.14725 3.24791L9.95525 5.56791H0.53125V7.03991H9.95525L8.14725 9.35991L6.16325 11.9999H7.87525L12.1793 6.30391L7.87525 0.60791Z" />
					</svg>
				</button>
			</div>
		);
	}

	renderThumbs() {
		const { infinite, children } = this.props;
		const { index } = this.state;
		const thumbChildren = this.createThumbs(children);

		if (thumbChildren.length < 2) return null;

		return (
			<div className={[s.slider_thumbs, 'u_slideThumbs'].join(' ')}>
				<div className={s.slider_thumbs_inner}>
					{
						thumbChildren.map((child, i) => <button type="button" key={`${child.key}Thumb`} className={[s.slider_thumbs_inner_thumb, (child.type === 'video' ? s.slider_thumbs_inner_thumb__film : null), (i === (index - infinite) ? s.slider_thumbs_inner_thumb__selected : null)].join(' ')} value={i} onClick={this.handleNavClick}>{ child }</button>)
					}
				</div>
			</div>
		);
	}

	render() {
		const { children, showArrows, showNav, showCounter, adaption, infinite, hint, thumbs, interactionTypes, transitionType } = this.props;
		const { index, transition, disabled, partialIndex, slideWidth } = this.state;

		// If no longer using Slider then remove.
		if (disabled) return children;

		const numberSlides = !disabled ? children.length + (infinite * 2) : children.length;

		const slidesStyles = {};
		if (!disabled && transitionType !== 'fade') {
			const translatePx = Math.round(slideWidth * partialIndex) * -1;
			if (hint) {
				slidesStyles.width = `${100 * numberSlides}vw`;
				slidesStyles.transform = `translateX(calc(${(-1 * index * 100) + (14 * index) + 7}vw + ${translatePx}px))`;
			} else {
				slidesStyles.width = `${100 * numberSlides}%`;
				slidesStyles.transform = `translateX(calc(${-1 * index * (100 / numberSlides)}% + ${translatePx}px))`;
			}
		}

		const slidesClasses = [s.slider_slides, 'u_slideMain'];
		if (transition) slidesClasses.push(s.slider_slides__transition);

		const sliderClasses = [s.slider];
		if (adaption !== false) sliderClasses.push(s[`slider__${adaption}`]);
		if (transitionType === 'fade') sliderClasses.push(s.slider__fade);
		if (hint) sliderClasses.push(s.slider__hint);

		let slideElements = React.Children.toArray(children);
		if (infinite && !disabled) {
			const startElements = React.Children.toArray(children).slice(-1 * infinite);
			const lastElements = React.Children.toArray(children).slice(0, infinite);

			slideElements = [
				...(React.Children.map(startElements, (startElement, i) => React.cloneElement(startElement, { key: `startElement_${i}` }))),
				...slideElements,
				...(React.Children.map(lastElements, (lastElement, i) => React.cloneElement(lastElement, { key: `lastElement_${i}` }))),
			];
		}

		const interactionEvents = {};
		if (!disabled) {
			interactionEvents.onTransitionEnd = this.handleTransitionEnd;

			if (interactionTypes.indexOf('touch') !== -1) {
				interactionEvents.onTouchStart = this.handleDragStart;
				interactionEvents.onTouchMove = this.handleDragMove;
				interactionEvents.onTouchEnd = this.handleDragEnd;
			}
			if (interactionTypes.indexOf('click') !== -1) {
				interactionEvents.onClick = (e) => this.handleClick(e, 1);
			}
			if (interactionTypes.indexOf('rightClick') !== -1) {
				interactionEvents.onContextMenu = (e) => this.handleClick(e, -1);
			}
		}

		return (
			<div className={sliderClasses.join(' ')} ref={this.slider}>
				{ showArrows && !disabled ? this.renderArrows() : null }
				{ showNav && !disabled ? this.renderNav() : null }
				{ showCounter && !disabled ? this.renderCounter() : null }
				<div className={s.slider_inner} ref={this.sliderInner} {...interactionEvents}>
					<div className={slidesClasses.join(' ')} style={slidesStyles}>
						{ slideElements }
					</div>
				</div>
				{ thumbs ? this.renderThumbs() : null }
			</div>
		);
	}
}

Slider.defaultProps = {
	selected: 0,
	startIndex: 0,
	infinite: 1, // 0, 1, 2
	hint: false,
	autoplay: false, // false or delay in ms before slide
	autoplayFilm: true, // boolean - should film automatically play or not.
	thumbs: false, // boolean
	showArrows: true, // boolean
	showNav: true, // boolean
	showCounter: false, // boolean
	adaption: false, // false or string
	transitionType: 'slide',
	transitionSpeed: 300,
	interactionTypes: ['touch'], // Array of strings - touch, click, rightClick
	screenMinWidthDisable: false, // false or integer - Disable slider under min width
	screenMaxWidthDisable: false, // false or integer - Disable slider over max width
};

export default hot(module)(Slider);
